import type { AuthRecord, UserRole } from "@/lib/dexie-db";

/**
 * Role-utility-helpers für UI-conditional-rendering.
 *
 * Server-side ist die finale source-of-truth (DB-trigger
 * users_traeger_admin_guard, JWT-claim-validation in middleware/auth.go),
 * aber UI-gates hier vermeiden 403-roundtrips für offensichtliche
 * "kann ich das?"-checks.
 *
 * Phase 15 / ADR-008.
 * B8/B9 permission-matrix — Phase 19.
 */

export type User = AuthRecord["user"];

/**
 * Minimaler Entry-Typ für canCorrectEntry — bewusst schmale Abstraktion:
 * nur das eine Feld das für die Betreuer-Eigentümer-Prüfung nötig ist.
 * Optional, weil created_by_user_id aktuell NICHT im by-room-API-contract
 * serialisiert wird (→ backend-gap, siehe notes in WORKNOTES). Fehlt das
 * Feld, verhält sich der Helper konservativ (betreuer sieht keinen Button).
 */
export interface EntryForPermission {
  /** Spiegelt entries.created_by_user_id; optional bis Backend das Feld liefert. */
  created_by_user_id?: string | null;
}

export function isTraegerAdmin(user: User | undefined | null): boolean {
  if (!user) return false;
  return user.role === "traeger_admin" && !!user.parentOrgId;
}

export function isAdmin(user: User | undefined | null): boolean {
  if (!user) return false;
  return user.role === "admin";
}

export function isLeitung(user: User | undefined | null): boolean {
  if (!user) return false;
  return user.role === "leitung";
}

/**
 * B9 — User einladen/anlegen: admin ODER leitung.
 * (leitung darf nur Rolle "betreuer" anlegen — das ist ein UI-constraint
 * im InviteModal/RoleSelect, nicht in diesem Helper; server enforced ebenfalls.)
 */
export function canManageOrgUsers(user: User | undefined | null): boolean {
  return isAdmin(user) || isLeitung(user);
}

/** B9 — Org-Stammdaten (PLZ, heat_officer): admin ONLY. Unveraendert. */
export function canManageOrgSettings(user: User | undefined | null): boolean {
  return isAdmin(user);
}

export function canAccessTraegerPortal(user: User | undefined | null): boolean {
  return isTraegerAdmin(user);
}

/**
 * B8 — Korrektur-CTA sichtbar?
 *
 * - admin / leitung:  jeder versiegelte Eintrag → true
 * - betreuer:         nur eigener Eintrag (created_by_user_id === user.id)
 *                     Falls das Feld fehlt (backend serialisiert es noch nicht)
 *                     → konservativ false (kein falscher 403-roundtrip).
 * - traeger_admin:    keine Korrektur → false
 * - nicht eingeloggt: false
 *
 * Der Server enforced die Regel zusätzlich via POST /v1/entries/{id}/correct
 * (403 "not_own_entry" für betreuer bei fremden Einträgen). Dieses UI-gate
 * dient nur der UX (offensichtliche 403 vermeiden), ist kein Security-Gate.
 */
export function canCorrectEntry(
  user: User | undefined | null,
  entry: EntryForPermission,
): boolean {
  if (!user) return false;
  if (isAdmin(user) || isLeitung(user)) return true;
  if (user.role === "betreuer") {
    // Feld fehlt → konservativ kein Button (verhindert garantierten 403).
    if (!entry.created_by_user_id) return false;
    return entry.created_by_user_id === user.userId;
  }
  return false;
}

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Administrator",
  leitung: "Leitung",
  betreuer: "Betreuer:in",
  traeger_admin: "Träger-Admin",
};

export const ROLE_BADGE_COLOR: Record<UserRole, string> = {
  admin: "bg-[color:var(--color-warm-orange-100)] text-[color:var(--color-warm-orange-900)]",
  leitung: "bg-[color:var(--color-cool-teal-100)] text-[color:var(--color-cool-teal-900)]",
  betreuer: "bg-neutral-100 text-neutral-700",
  traeger_admin: "bg-[color:var(--color-heat-2-gradient-from)] text-neutral-900",
};
